import type { BillingEntitlementKey } from '@/constants';
import { EXTERNAL_LINKS } from '@/shared/constants/externalLinks';

export interface BillingEntitlementDefinition {
    description: string;
    documentationUrl: string;
    label: string;
}

const documentationUrl = (path: `/${string}` = '/') =>
    `${EXTERNAL_LINKS.documentation.home}${path === '/' ? '' : path}`;

/**
 * Canonical presentation metadata for every entitlement surfaced by billing UI.
 * Product copy, documentation associations, and hover explanations should resolve
 * through this registry so pricing surfaces cannot drift independently.
 */
export const BILLING_ENTITLEMENT_CATALOG = {
    investments: {
        description: 'Investment research records and their associated diligence data.',
        documentationUrl: documentationUrl('/fundamentals/investments'),
        label: 'Investments',
    },
    memos: {
        description: 'Research memos for capturing, collaborating on, and sharing investment findings.',
        documentationUrl: documentationUrl('/fundamentals/memos'),
        label: 'Memos',
    },
    folders: {
        description: 'Workspace folders for organizing related investment research and diligence resources.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'Folders',
    },
    pipelines: {
        description: 'Stage-based workflows for organizing and monitoring an investment process.',
        documentationUrl: documentationUrl('/fundamentals/pipelines'),
        label: 'Pipelines',
    },
    teams: {
        description: 'Workspace teams that group members and their shared resources.',
        documentationUrl: documentationUrl('/fundamentals/teams'),
        label: 'Teams',
    },
    watchlists: {
        description: 'Saved groups of companies and resources monitored together.',
        documentationUrl: documentationUrl('/fundamentals/search'),
        label: 'Watchlists',
    },
    tags: {
        description: 'Reusable labels for organizing companies and workspace resources.',
        documentationUrl: documentationUrl('/fundamentals/search'),
        label: 'Tags',
    },
    subteamsPerTeam: {
        description: 'Nested groups used to assign focused access within a team.',
        documentationUrl: documentationUrl('/fundamentals/teams'),
        label: 'Subteams per Team',
    },
    dashboards: {
        description: 'Reusable reporting surfaces for monitoring workspace research and analysis.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'Dashboards',
    },
    lists: {
        description: 'Structured lists for managing relationship, company, and custom operating records.',
        documentationUrl: documentationUrl('/extras/enrichment-data'),
        label: 'Lists',
    },
    'data-export': {
        description: 'Download supported research and historical data for analysis outside the platform.',
        documentationUrl: documentationUrl('/extras/exports'),
        label: 'Data Downloads',
    },
    prioritySupport: {
        description: 'Prioritized assistance from the Countercyclical support team.',
        documentationUrl: documentationUrl('/extras/troubleshooting'),
        label: 'Priority Support',
    },
    rbac: {
        description: 'Role-based permissions that govern member access within a workspace.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'Role-Based Access Controls',
    },
    dataExport: {
        description: 'Export supported workspace data for reporting, portability, and downstream analysis.',
        documentationUrl: documentationUrl('/extras/exports'),
        label: 'Data Export',
    },
    tfa: {
        description: 'Add a second authentication factor to strengthen account security.',
        documentationUrl: documentationUrl('/extras/security'),
        label: 'Two-Factor Authentication',
    },
    apiAndWebhooks: {
        description: 'Integrate with Countercyclical through authenticated APIs and real-time webhook events.',
        documentationUrl: documentationUrl('/developers/getting-started'),
        label: 'API & Webhooks',
    },
    sso: {
        description: 'Use an organization-managed identity provider for secure single sign-on.',
        documentationUrl: documentationUrl('/fundamentals/settings/account/general'),
        label: 'Single Sign-On (SSO)',
    },
    scim: {
        description: 'Provision and deprovision workspace identities through a centralized directory.',
        documentationUrl: documentationUrl('/fundamentals/settings/workspace/permissions'),
        label: 'SCIM Provisioning',
    },
    auditLogs: {
        description: 'Review workspace activity records for operational oversight and accountability.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'Audit Logs',
    },
    betaProgram: {
        description: 'Request access to eligible pre-release platform capabilities and provide product feedback.',
        documentationUrl: documentationUrl(),
        label: 'Beta Feature Program',
    },
    migrationAssistance: {
        description: 'Receive guided assistance moving existing research and data into Countercyclical.',
        documentationUrl: documentationUrl('/extras/imports'),
        label: 'Migration Assistance',
    },
    initiatives: {
        description: 'Govern recurring diligence initiatives with defined ownership, evidence, and outcomes.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'Governed Lifecycle Initiatives',
    },
    actions: {
        description: 'Automate repeatable lifecycle actions across recurring research and diligence workflows.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'Automated Lifecycle Actions',
    },
    auditReady: {
        description: 'Preserve activity and collaboration context for reviewable, audit-ready workflows.',
        documentationUrl: documentationUrl('/extras/security'),
        label: 'Audit-Ready Activity & Collaboration',
    },
    biAnalytics: {
        description: 'Create reusable dashboards and reports for business intelligence analysis.',
        documentationUrl: documentationUrl('/fundamentals/workspaces'),
        label: 'BI Analytics',
    },
    dataEnrichment: {
        description: 'Enrich company and people records with supported external data sources.',
        documentationUrl: documentationUrl('/extras/enrichment-data'),
        label: 'Premium Data Enrichment',
    },
    emailAndCalendar: {
        description: 'Connect supported email and calendar providers as relationship data sources.',
        documentationUrl: documentationUrl('/guides/integrations'),
        label: 'Email & Calendar Data Sources',
    },
    vantageQueries: {
        description: 'Queries executed through Countercyclical’s governed investment research query layer.',
        documentationUrl: documentationUrl('/fundamentals/search'),
        label: 'Vantage Queries',
    },
    relationsListRecords: {
        description: 'People, company, and custom records synchronized through connected data sources.',
        documentationUrl: documentationUrl('/extras/enrichment-data'),
        label: 'List Records',
    },
    agenticWorkflows: {
        description: 'Credit-powered workflows that coordinate assisted investment research tasks.',
        documentationUrl: documentationUrl('/fundamentals/search'),
        label: 'Agentic Workflows',
    },
    complianceWorkflows: {
        description: 'Credit-powered workflows that support repeatable compliance review processes.',
        documentationUrl: documentationUrl('/extras/security'),
        label: 'Compliance Workflows',
    },
} satisfies Record<BillingEntitlementKey, BillingEntitlementDefinition>;

export const getBillingEntitlementDefinition = (key: BillingEntitlementKey): BillingEntitlementDefinition =>
    BILLING_ENTITLEMENT_CATALOG[key];

export const getEnabledBillingEntitlementKeys = <TKey extends BillingEntitlementKey>(
    entitlements: Partial<Record<TKey, boolean>>,
): TKey[] =>
    (Object.entries(entitlements) as Array<[TKey, boolean | undefined]>)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);
